import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Warning, RestartAlt } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { apiClient } from '../../utils/apiClient';

interface ActiveSession {
  sessionId: string;
  connectorId: number;
  status: string;
}

interface Charger {
  id: string;
  name: string;
  stationId: string;
  status: string;
  isConnected: boolean;
  siteId?: string;
}

interface ChargerRebootDialogProps {
  open: boolean;
  charger: Charger;
  activeSessions?: ActiveSession[];
  onClose: () => void;
  onSuccess: () => void;
}

interface RebootReason {
  value: string;
  label: string;
  description: string;
}

const REBOOT_REASONS: RebootReason[] = [
  {
    value: 'unresponsive',
    label: 'Charger Unresponsive',
    description: 'Charger is not responding to commands or appearing offline'
  },
  {
    value: 'firmware_update',
    label: 'Firmware Update',
    description: 'Reboot required after firmware update or configuration change'
  },
  {
    value: 'network_issue',
    label: 'Network Connectivity Issue',
    description: 'Charger experiencing network problems or connection drops'
  },
  {
    value: 'emergency',
    label: 'Emergency Maintenance',
    description: 'Urgent reboot required for safety or critical maintenance'
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Other reason (please specify in notes)'
  },
];

const ChargerRebootDialog: React.FC<ChargerRebootDialogProps> = ({
  open,
  charger,
  activeSessions = [],
  onClose,
  onSuccess,
}) => {
  const notify = useNotify();
  const [resetType, setResetType] = useState<'Soft' | 'Hard'>('Soft');
  const [forceReboot, setForceReboot] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const hasActiveSessions = activeSessions.length > 0;

  // Validation
  const canReboot = useMemo(() => {
    // Must have reason
    if (!reason) return false;

    // If no active sessions, always allow
    if (!hasActiveSessions) return true;

    // If active sessions exist, must enable force reboot
    return forceReboot;
  }, [reason, hasActiveSessions, forceReboot]);

  const handleResetTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setResetType(event.target.value as 'Soft' | 'Hard');
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReason(event.target.value);
  };

  const handleForceRebootChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForceReboot(event.target.checked);
  };

  const handleReboot = async () => {
    if (!canReboot) return;

    try {
      setLoading(true);

      // Step 1: Send reboot command
      const response = await apiClient.post(
        `/api/backend/chargers/${charger.id}/reboot`,
        {
          resetType,
          forceReboot,
          reason,
          notes: notes.trim() || undefined,
        }
      ) as { success?: boolean; ocppStatus?: string; auditId?: string; message?: string };

      // Check if reboot was rejected
      if (!response.success || response.ocppStatus === 'Rejected') {
        notify('Charger rejected the reboot command', { type: 'error' });
        return;
      }

      const auditId = response.auditId;
      if (!auditId) {
        notify(response.message || 'Reboot command sent successfully', { type: 'success' });
        onSuccess();
        onClose();
        return;
      }

      // Show initial notification
      notify('Reboot command accepted. Waiting for charger to restart...', { type: 'info' });

      // Step 2: Poll for completion
      const maxWait = 180000; // 3 minutes
      const startTime = Date.now();
      const pollInterval = 5000; // 5 seconds

      while (Date.now() - startTime < maxWait) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));

        try {
          const status = await apiClient.get(
            `/api/backend/chargers/${charger.id}/reboot/${auditId}`
          ) as {
            status: string;
            message?: string;
            timeSyncStatus?: string;
            clockDriftSeconds?: number;
            elapsedSeconds?: number;
          };

          if (status.status === 'completed') {
            // Success!
            notify('Charger rebooted successfully!', { type: 'success' });

            // Check time sync status
            if (status.timeSyncStatus === 'out_of_sync') {
              notify(
                `Warning: Charger clock is still out of sync by ${Math.floor((status.clockDriftSeconds || 0) / 60)} minutes. Please configure NTP on the charger.`,
                { type: 'warning' }
              );
            } else if (status.timeSyncStatus === 'warning') {
              notify(
                'Note: Minor clock drift detected. Recommend configuring NTP on the charger.',
                { type: 'info' }
              );
            }

            onSuccess();
            onClose();
            return;
          }

          if (status.status === 'timeout') {
            notify(
              'Charger did not come back online after reboot. Please check charger status manually.',
              { type: 'warning' }
            );
            onSuccess(); // Refresh charger list
            onClose();
            return;
          }

          if (status.status === 'rejected' || status.status === 'error') {
            notify(
              status.message || 'Reboot failed',
              { type: 'error' }
            );
            onSuccess();
            onClose();
            return;
          }

          // Still pending - update progress in console
          console.log(`Reboot in progress... ${status.elapsedSeconds}s elapsed`);

        } catch (pollError: any) {
          console.error('Error polling reboot status:', pollError);
          // Continue polling on error
        }
      }

      // Max wait time exceeded
      notify(
        'Reboot status check timed out. The charger may still be rebooting. Please check manually.',
        { type: 'warning' }
      );
      onSuccess();
      onClose();

    } catch (error: any) {
      console.error('Reboot error:', error);

      if (error.status === 409) {
        // Active sessions blocking
        notify(
          error.body?.error || 'Cannot reboot - active charging sessions. Please wait or enable force reboot.',
          { type: 'warning' }
        );
      } else if (error.status === 504) {
        // Timeout
        notify(
          'Timeout waiting for charger response. The charger may be offline or experiencing network issues. Please try again.',
          { type: 'error' }
        );
      } else if (error.status === 400) {
        // Charger offline
        notify(
          error.body?.error || 'Cannot send reboot command to offline charger',
          { type: 'error' }
        );
      } else {
        // Generic error
        notify(
          error.body?.error || 'Failed to reboot charger. Please try again.',
          { type: 'error' }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form
      setResetType('Soft');
      setForceReboot(false);
      setReason('');
      setNotes('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <RestartAlt color="warning" />
          <Typography variant="h6">Reboot Charger</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {charger.name} ({charger.stationId})
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {/* Reset Type Selection */}
          <FormControl component="fieldset">
            <FormLabel component="legend">Reset Type</FormLabel>
            <RadioGroup
              value={resetType}
              onChange={handleResetTypeChange}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="Soft"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Soft Reset (Recommended)</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Gracefully restart charger software. Faster and less disruptive.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="Hard"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Hard Reset</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Full hardware reboot. Use if charger is completely unresponsive.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          <Divider />

          {/* Active Sessions Warning */}
          {hasActiveSessions && (
            <Alert severity="warning" icon={<Warning />}>
              <Typography variant="subtitle2" gutterBottom>
                Active Charging Sessions Detected
              </Typography>
              <Typography variant="body2" gutterBottom>
                This charger has {activeSessions.length} active charging session(s):
              </Typography>
              <List dense sx={{ mt: 1 }}>
                {activeSessions.map((session) => (
                  <ListItem key={session.sessionId} sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={`Connector ${session.connectorId}: ${session.status}`}
                      secondary={`Session: ${session.sessionId}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Reboot is blocked for safety. Wait for sessions to complete or enable force reboot below.
              </Typography>
            </Alert>
          )}

          {/* Force Reboot Checkbox */}
          {hasActiveSessions && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={forceReboot}
                  onChange={handleForceRebootChange}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Force reboot (ignore active sessions)</Typography>
                  <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Warning fontSize="small" />
                    This will immediately interrupt ongoing charging sessions. Use only in emergencies.
                  </Typography>
                </Box>
              }
            />
          )}

          {/* Divider only shows if there are active sessions */}
          {hasActiveSessions && <Divider />}

          {/* Reason Selection */}
          <FormControl component="fieldset" required>
            <FormLabel component="legend">Reason *</FormLabel>
            <RadioGroup
              value={reason}
              onChange={handleReasonChange}
              sx={{ mt: 1 }}
            >
              {REBOOT_REASONS.map((r) => (
                <FormControlLabel
                  key={r.value}
                  value={r.value}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">{r.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.description}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Notes Field */}
          <TextField
            label="Additional Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            placeholder="Additional details about why reboot is needed..."
            fullWidth
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleReboot}
          variant="contained"
          color="warning"
          disabled={!canReboot || loading}
          startIcon={<RestartAlt />}
        >
          {loading ? 'Rebooting...' : 'Reboot Charger'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChargerRebootDialog;
